/**
 * Define utilities for traversing pipeline DAGs to determine the lineage of top-level datasets and views.
 *
 * In this context, lineage is generally a sub-set of a DAG used to render a single top-level dataset or view.
 * This is usually a linked list within a DAG. Lineage refers to both child lineage and ancestry (i.e. parent lineage).
 */
import {ANALYSIS_OUTPUT_STEP_TYPES} from 'analysis-tool/AnalysisToolConstants';
import {
  GetAnalysisPipelineStep,
  GetAnalysisDataFrame,
  GetAnalysisStepData,
  isAnalysisOutputStepType,
} from 'analysis-tool/AnalysisTypes';
import {assert} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {getChildSteps, getParentSteps} from 'pipelines/StepLineageUtils';

import {getOutputStepDataFromStep} from './getStepDataFromStep';
import getLegacyStepFromAnalysis from './getStepFromAnalysis';
import {
  isTableInterStepType,
  isTableStartStepType,
  isViewTerminalStepType,
  isTopLevelTableStartStepType,
  isViewStartStepType,
} from './LineageStepTypes';

export interface StepDataFrameLineageNode {
  step: GetAnalysisPipelineStep;
  data?: {
    outputStepData: GetAnalysisStepData;
    dataFrame: GetAnalysisDataFrame;
  };
}

export type StepDataFrameLineageTree = Record<string, Array<StepDataFrameLineageNode>>;

/**
 * Keep in sync with benchling/pipelines/steps/step_lineage_utils.py
 */
export const LINEAGE_TERMINAL_STEP_TYPES = [
  ...ANALYSIS_OUTPUT_STEP_TYPES,
  PipelineStepType.DATASET_VIEW,
  PipelineStepType.JOIN,
  PipelineStepType.UNION_DATASETS,
  PipelineStepType.PROMOTE_DATASET,
  PipelineStepType.PROCEDURE_RUN_DATA_FRAMES,
  PipelineStepType.TEMPLATE_INPUT,
  PipelineStepType.VARIABLE_TEMPLATE_INPUT_TABLE,
  PipelineStepType.THIRD_PARTY,
  PipelineStepType.LOOK_UP,
];

/** @package */
export const MAX_LOOP_ITERATION = 1000;

const TREE_KEY_SEPARATOR = '--';

/** @package */
export function lineageNodeToTreeKey(node: StepDataFrameLineageNode): string {
  const suffix =
    node.data != null
      ? `${TREE_KEY_SEPARATOR}${node.data.outputStepData.id}${TREE_KEY_SEPARATOR}${node.data.dataFrame.id}`
      : '';
  return `${node.step.id}${suffix}`;
}

/** @package */
export function lineageTreeKeyToNode(pipeline: PipelineGraphQL, key: string): StepDataFrameLineageNode {
  const [stepId, maybeStepDataId, maybeDataFrameId] = key.split(TREE_KEY_SEPARATOR);

  const step = getLegacyStepFromAnalysis(pipeline, stepId);
  const maybeOutputStepData =
    maybeStepDataId != null ? getOutputStepDataFromStep(step, maybeStepDataId) : null;
  const maybeDataFrame =
    maybeOutputStepData != null
      ? maybeOutputStepData.dataFrames.find((dataFrame) => dataFrame.id === maybeDataFrameId)
      : null;

  return {
    step,
    ...(maybeOutputStepData != null
      ? {data: {outputStepData: maybeOutputStepData, dataFrame: maybeDataFrame!}}
      : {}),
  };
}

export function areStepDataFrameLineageNodesEqual(
  node1: StepDataFrameLineageNode,
  node2: StepDataFrameLineageNode
): boolean {
  return lineageNodeToTreeKey(node1) === lineageNodeToTreeKey(node2);
}

/**
 * Used to create either a child or ancestor lineage tree.
 */
function createLineageTree(
  getNextSteps: (pipeline: PipelineGraphQL, node: StepDataFrameLineageNode) => Array<GetAnalysisPipelineStep>,
  pipeline: PipelineGraphQL,
  startingLineageNode: StepDataFrameLineageNode,
  childNodeFilter: (parentNode: StepDataFrameLineageNode, childNode: StepDataFrameLineageNode) => boolean,
  /**
   * Whether to include terminal step types in the children of a lineage node. This is false for `getChildLineageForDataset`
   * and true for `getParentLineageForDataset`. A terminal step type should be considered a leaf node when getting
   * child steps in a lineage (i.e., in `getChildLineageForDataset`); however, because we are traversing backward
   * when we get the ancestor steps in a lineage, we should include terminal step types in the inverted child->parent map
   * formed in `getParentLineageForDataset`.
   */
  includeTerminalStepInChildren: boolean,
  /**
   * Whether to only traverse the graph based on step definition links, rather than step definition +
   * step data links. This is useful for traversing a pipeline that is running or failed, where the input
   * and output step datas may not yet be fully filled.
   */
  onlyTraverseSteps: boolean = false,
  /**
   * Whether to include terminal step types in the lineage. If true, then we will recurse on linear terminal step
   * types to continue to build the lineage; otherwise, these steps will be treated as leaf steps with no descendants.
   * Note that if this is set to true, it overrides a false `includeTerminalStepInChildren` flag. This option is given
   * to the caller because `getChildLineageForDataset` and `getParentLineageForDataset` are used to get all linear
   * child or ancestor steps (see `StepLineageUtils.ts`), and step ancestry, unlike data frame ancestry, may extend
   * beyond terminal step types.
   */
  includeTerminalSteps: boolean = false
): StepDataFrameLineageTree {
  const tree: StepDataFrameLineageTree = {};

  let queue = [startingLineageNode];
  let iteration = 0;
  while (queue.length > 0) {
    assert(
      iteration < MAX_LOOP_ITERATION,
      `Loop exceeded ${MAX_LOOP_ITERATION} iterations, possibly looping infinitely.`
    );

    let newQueue: Array<StepDataFrameLineageNode> = [];

    for (const node of queue) {
      let childNodes: Array<StepDataFrameLineageNode> = [];

      // For each child of node, gather all its child nodes
      for (const childStep of getNextSteps(pipeline, node)) {
        if (onlyTraverseSteps) {
          // If only traversing steps, then we don't need to look at the output step data
          childNodes.push({step: childStep});
        } else {
          // Otherwise, we need to create a node for each unique <step - data frame> pair
          const childDataFrameNodes = childStep.outputStepDatas
            .flatMap((stepData) =>
              stepData.dataFrames.map((dataFrame) => ({
                step: childStep,
                data: {outputStepData: stepData, dataFrame},
              }))
            )
            .filter((childNode) => childNodeFilter(node, childNode));

          // If there are no shared data frames between the child and parent (e.g., the step is RUNNING),
          // then we add a single node without a data frame specified
          childNodes = childNodes.concat(
            childDataFrameNodes.length > 0 ? childDataFrameNodes : [{step: childStep}]
          );
        }
      }

      const newChildNodes = childNodes.filter(
        (childNode) =>
          includeTerminalSteps ||
          includeTerminalStepInChildren ||
          !LINEAGE_TERMINAL_STEP_TYPES.includes(childNode.step.definition.stepType)
      );

      // Add node to the tree
      tree[lineageNodeToTreeKey(node)] = newChildNodes;

      // Add child nodes to the next queue, filtering again without the `includeTerminalStepInChildren` flag
      newQueue = newQueue
        .concat(newChildNodes)
        .filter(
          (childNode) =>
            includeTerminalSteps || !LINEAGE_TERMINAL_STEP_TYPES.includes(childNode.step.definition.stepType)
        );
    }

    // Recurse on the next queue
    queue = newQueue;
    iteration += 1;
  }

  return tree;
}

/**
 * Inverts the parent-child relationships in a lineage tree.
 *
 * Used to return a parent->child ancestor tree rather than the child->parent tree that `createLineageTree`
 * generates.
 */
function invertLineageTree(
  pipeline: PipelineGraphQL,
  tree: StepDataFrameLineageTree
): StepDataFrameLineageTree {
  const invertedTree: StepDataFrameLineageTree = {};

  for (const [childNodeKey, parentNodes] of Object.entries(tree)) {
    const childNode = lineageTreeKeyToNode(pipeline, childNodeKey);

    if (parentNodes.length === 0 && !invertedTree.hasOwnProperty(childNodeKey)) {
      invertedTree[childNodeKey] = [];
    }

    for (const parent of parentNodes) {
      const parentKey = lineageNodeToTreeKey(parent);

      if (invertedTree.hasOwnProperty(parentKey)) {
        invertedTree[parentKey].push(childNode);
      } else {
        invertedTree[parentKey] = [childNode];
      }
    }
  }

  return invertedTree;
}

export enum TableLineageType {
  TOP_LEVEL_TABLE = 'TOP_LEVEL_TABLE',
  VIEW = 'VIEW',
}

export interface TableLineageInformation {
  nodes: Array<StepDataFrameLineageNode>;
  type: TableLineageType | null;
}

/**
 * Get the child step of the `step` that belongs to the table or view lineage.
 *
 * Because a table/view represents a single table with zero or more transformations performed on it, then
 * we know that a table/view lineage must be a linear sequence of pipeline steps. If `step` has multiple
 * children, then only one child references the "single table with zero or more transformations" whereas
 * the other children must be part of other table views. (For example, a regression step may have child
 * interpolation steps, but these interpolation steps do not belong to the same lineage as the regression.)
 * This function will filter child steps by whether they fit the definition of an inter-step or a terminal
 * step of a table lineage.
 */
function getNextChildTableStep(
  pipeline: PipelineGraphQL,
  {step, data}: StepDataFrameLineageNode
): Array<GetAnalysisPipelineStep> {
  // Check whether we are at the end of a VIEW lineage
  if (isViewTerminalStepType(step.definition.stepType)) {
    return [];
  }

  // If data is specified, then filter child steps by whether they take the output data as input
  const childSteps = getChildSteps(pipeline, step).filter(
    (step) =>
      data == null ||
      step.inputStepDatas.some((inputStepData) =>
        inputStepData.dataFrames.some((dataFrame) => dataFrame.id === data.dataFrame.id)
      )
  );

  const nextInterSteps = childSteps.filter((childStep) =>
    isTableInterStepType(childStep.definition.stepType)
  );

  // If there are no inter-table step type children, then the children must signify the end of
  // a table/view. We can use the type(s) of the child steps to determine the table type (top-level or view).
  if (nextInterSteps.length === 0) {
    const maybeEndViewSteps = childSteps.filter((childStep) =>
      isViewTerminalStepType(childStep.definition.stepType)
    );
    return maybeEndViewSteps.length <= 1 ? maybeEndViewSteps : [];
  }

  assert(
    nextInterSteps.length === 1,
    'Expected exactly one child step to fit the definition of an inter-table node step.',
    {
      pipelineId: pipeline.id,
      stepId: step.id,
      childStepsIds: childSteps.map(({id}) => id),
      nextInterStepsIds: nextInterSteps.map(({id}) => id),
    }
  );

  return nextInterSteps;
}

/**
 * Get the table type (if inferrable) and the ordered sequence of descendant nodes of a given `node`
 * (inclusive).
 *
 * Also see `getTableLineageDescendantSteps`, which calls this function but takes in and outputs steps
 * rather than nodes.
 */
export function getTableLineageDescendantInformation(
  pipeline: PipelineGraphQL,
  node: StepDataFrameLineageNode
): TableLineageInformation {
  // Because of how `getNextChildTableStep` is defined, this tree will be a linear sequence of steps,
  // so it is safe to call `linearizeDataFrameLineageTree` on the tree below.
  const tree = createLineageTree(
    getNextChildTableStep,
    pipeline,
    node,
    // In the parentStep -> childStep lineage, all the data frame nodes of a childStep are valid
    // nodes of the parentStep's data frame node
    (parentStepNode, childStepNode) => true,
    true,
    false,
    true
  );
  const nodes = linearizeDataFrameLineageTree(pipeline, tree, true);
  const lastStep = nodes[nodes.length - 1].step;

  let type: TableLineageType | null = null;
  if (isViewTerminalStepType(lastStep.definition.stepType)) {
    type = TableLineageType.VIEW;
  } else {
    type = TableLineageType.TOP_LEVEL_TABLE;
  }

  return {type, nodes};
}

/**
 * Get the parent step of the `step` that belongs to the table or view lineage.
 *
 * Because a table/view represents a single table with zero or more transformations performed on it, then
 * we know that a table/view lineage must be a linear sequence of pipeline steps. If `step` has multiple
 * parents, then only one parent references the "single table with zero or more transformations" whereas
 * the other parents only dictate how the table will be transformed. So this function will filter parent
 * steps by whether they fit the definition of an inter-step or a start step of a table lineage.
 */
function getNextParentTableStep(
  pipeline: PipelineGraphQL,
  {step}: StepDataFrameLineageNode
): Array<GetAnalysisPipelineStep> {
  // Check whether we are at the start of a table lineage
  if (isTableStartStepType(step.definition.stepType)) {
    return [];
  }

  const parentSteps = getParentSteps(pipeline, step);

  if (isAnalysisOutputStepType(step.definition.stepType)) {
    const nextTerminalSteps = parentSteps.filter((parentStep) =>
      isViewTerminalStepType(parentStep.definition.stepType)
    );
    if (nextTerminalSteps.length === 1) {
      return getNextParentTableStep(pipeline, {step: nextTerminalSteps[0]});
    }
  }

  const nextInterSteps = parentSteps.filter((parentStep) =>
    isTableInterStepType(parentStep.definition.stepType)
  );

  // If there are no inter-table step type parents, then the parent step must be the start of
  // a table/view. We can use the type of the parent step to determine the table type (top-level or view)
  if (nextInterSteps.length === 0) {
    const startSteps = parentSteps.filter(
      (parentStep) =>
        isTopLevelTableStartStepType(parentStep.definition.stepType) ||
        isViewStartStepType(parentStep.definition.stepType)
    );
    assert(
      startSteps.length === 1,
      'Expected exactly one parent step to fit the definition of a start of a table node'
    );

    return startSteps;
  }

  assert(
    nextInterSteps.length === 1,
    'Expected exactly one parent step to fit the definition of an inter-table or terminal node step.'
  );

  return nextInterSteps;
}

/**
 * Get the table type (if inferrable) and the ordered sequence of ancestor nodes of a given `node`
 * (inclusive).
 *
 * Also see `getTableLineageAncestorSteps`, which calls this function but takes in and outputs steps
 * rather than nodes.
 */
export function getTableLineageAncestorInformation(
  pipeline: PipelineGraphQL,
  node: StepDataFrameLineageNode
): TableLineageInformation {
  // Because of how `getNextParentTableStep` is defined, this tree will be a linear sequence of steps,
  // so it is safe to call `linearizeDataFrameLineageTree` on the tree below.
  const tree = invertLineageTree(
    pipeline,
    createLineageTree(
      getNextParentTableStep,
      pipeline,
      node,
      // In the childStep -> parentStep lineage, the only valid nodes of the childStep's data frame
      // node are parentStep data frame nodes whose dataFrame is an input data frame to the childStep
      (childStepNode, parentStepNode) =>
        parentStepNode.data != null &&
        childStepNode.step.inputStepDatas
          .flatMap((stepData) => stepData.dataFrames)
          .map((dataFrame) => dataFrame.id)
          .includes(parentStepNode.data.dataFrame.id),
      true,
      false,
      true
    )
  );
  const nodes = linearizeDataFrameLineageTree(pipeline, tree, true);

  let type: TableLineageType | null = null;
  if (isTopLevelTableStartStepType(nodes[0].step.definition.stepType)) {
    type = TableLineageType.TOP_LEVEL_TABLE;
  } else if (isViewStartStepType(nodes[0].step.definition.stepType)) {
    type = TableLineageType.VIEW;
  }

  return {type, nodes};
}

/**
 * Convert a lineage tree into an ordered list of lineage nodes.
 *
 * The last node in the ordered list will either be a node with no children or a node with multiple children,
 * as this function does not make assumptions about how to linearize multiple descendants.
 *
 * NOTE: This is package-private because it should only be used when the caller can guarantee that the
 * input `tree` is linear, or to test other functions found in this file. Because this function raises
 * when it encounters a step lineage with multiple roots, it's not very safe for use in the product.
 * See `StepLineageUtils.ts` for helper functions that return linear sequences of steps.
 *
 * @example
 * >>> const tree = {
 *   {dataset_view_step_1, df_dvs1}: [{filter_dataset_step_3, df_fds3}],
 *   {filter_dataset_step_3, df_fds3}: [{regression_step, df_rs-1}, {regression_step, df_rs-2}],
 *   {regression_step, df_rs-1}: [],
 *   {regression_step, df_rs-2}: [],
 * }
 *
 * >>> linearizeDataFrameLineageTree(pipeline, tree)
 * [{dataset_view_step_1, df_dvs1}, {filter_dataset_step_3, df_fds3}]
 *
 * @package
 */
export function linearizeDataFrameLineageTree(
  pipeline: PipelineGraphQL,
  tree: StepDataFrameLineageTree,
  onlyTraverseSteps: boolean = false
): Array<StepDataFrameLineageNode> {
  const output: Array<StepDataFrameLineageNode> = [];

  const roots = getRootStepDataFrameLineageNodes(pipeline, tree);
  assert(
    roots.length === 1,
    'This function should only be called on a data frame lineage tree that has exactly one root.',
    {length: roots.length}
  );

  let [currentNode] = roots;
  let iteration = 0;
  while (iteration < MAX_LOOP_ITERATION) {
    output.push(currentNode);
    iteration += 1;

    const nextNodes = tree[lineageNodeToTreeKey(currentNode)];
    if (nextNodes == null || nextNodes.length === 0) {
      break;
    } else if (onlyTraverseSteps && nextNodes.length > 1) {
      // If all next nodes come from the same step and we are only traversing steps, then we can
      // treat these nodes as a single data-less node and continue to linearize.
      const {step} = nextNodes[0];
      if (
        nextNodes.every((node) => node.step.id === step.id) &&
        // Prevent recursing on the same step more than once
        output.every((seenNode) => seenNode.step.id !== step.id)
      ) {
        currentNode = {step};
        continue;
      } else {
        break;
      }
    }

    [currentNode] = nextNodes;
  }

  // The loop will break if iteration reaches MAX_LOOP_ITERATION, but even so this indicates that
  // something has probably gone wrong, since the tree should not be anywhere close to 1000 steps long
  assert(
    iteration < MAX_LOOP_ITERATION,
    `Loop exceeded ${MAX_LOOP_ITERATION} iterations, possibly looping infinitely.`
  );

  return output;
}

/**
 * Get a list of root nodes in a step data frame lineage tree.
 *
 * @returns a list of the nodes in the lineage tree that are not also found as children of other
 * nodes in the tree.
 */
export function getRootStepDataFrameLineageNodes(
  pipeline: PipelineGraphQL,
  tree: StepDataFrameLineageTree
): Array<StepDataFrameLineageNode> {
  // We operate on keys rather than objects to avoid duplicates and allow `includes` checks
  let knownRootKeys: Set<string> = new Set<string>();
  let knownChildKeys: Set<string> = new Set<string>();

  for (const [parentNodeKey, childNodes] of Object.entries(tree)) {
    const updatedKnownChildKeys = new Set<string>([
      ...knownChildKeys,
      ...childNodes.map((childNode) => lineageNodeToTreeKey(childNode)),
    ]);

    const filteredKnownRootKeys = new Set<string>(
      [...knownRootKeys, parentNodeKey].filter(
        (possibleRootKey) => !updatedKnownChildKeys.has(possibleRootKey)
      )
    );

    knownRootKeys = filteredKnownRootKeys;
    knownChildKeys = updatedKnownChildKeys;
  }

  return [...knownRootKeys].map((rootKey) => lineageTreeKeyToNode(pipeline, rootKey));
}
